import {
  FEATURE,
  FEATURE_PARAMS,
  DEBUG_FEATURE,
  areParamsValid,
  convertParamsToNamed,
  convertNamedToParams,
} from '@wix/deeplink-core';

import type {
  Feature,
  FeatureType,
  Params,
  NamedParams,
  FeatureWithNamed,
} from '@wix/deeplink-core';

type FeatureWithParamsAndNamed<T extends FeatureType> = T extends FeatureType
  ? Feature<T> & FeatureWithNamed<T>
  : never;

export type FeatureMapping<T extends FeatureType> = {
  check?(feature: FeatureWithParamsAndNamed<T>): boolean | Promise<boolean>;
  show(feature: FeatureWithParamsAndNamed<T>): void | Promise<void>;
};

type RegisterMapping = {
  [K in FeatureType]: FeatureMapping<K>;
};

// DO NOT EXPORT `registeredFeatures`!
// Otherwise, you'll have to deal with stale references after feature re-register
const registeredFeatures: Partial<RegisterMapping> = {};

function isValidMapping<T extends FeatureType>(
  mapping?: Record<string, unknown>,
): mapping is FeatureMapping<T> {
  if (!mapping) {
    return false;
  }

  if (!mapping.show) {
    return false;
  }

  if (typeof mapping.show !== 'function') {
    return false;
  }

  if (mapping.check != null && typeof mapping.check !== 'function') {
    return false;
  }

  return true;
}

export class UnknownFeatureError extends Error {}

export class InvalidMappingError extends Error {}

function isFeatureType(v: any): v is FeatureType {
  return v in FEATURE;
}

export function register(featuresMap: Partial<RegisterMapping>): void {
  const unknown: string[] = [];
  const invalidMapping: FeatureType[] = [];
  const okayToRegister: Partial<RegisterMapping> = {};

  const featureMapKeys: (string | typeof DEBUG_FEATURE)[] =
    Object.keys(featuresMap);
  // `Object.keys` doesn't include `Symbol`s
  if (DEBUG_FEATURE in featuresMap) {
    featureMapKeys.push(DEBUG_FEATURE);
  }

  for (const key of featureMapKeys) {
    if (!isFeatureType(key)) {
      unknown.push(key);
      continue;
    }

    const value = featuresMap[key];
    type T = typeof key;

    if (!isValidMapping<T>(value)) {
      invalidMapping.push(key);
      continue;
    }

    okayToRegister[key] = value.check
      ? {
          check: value.check,
          show: value.show,
        }
      : { show: value.show };
  }

  if (unknown.length) {
    throw new UnknownFeatureError(
      `[deeplink] Trying to register unknown features: ${unknown.join(', ')}`,
    );
  }

  if (invalidMapping.length) {
    throw new InvalidMappingError(
      `[deeplink] Features have invalid methods mapping: ${invalidMapping.join(
        ', ',
      )}`,
    );
  }

  Object.assign(registeredFeatures, okayToRegister);
}

export async function check<T extends FeatureType>(
  feature: Feature<T> | FeatureWithNamed<T>,
): Promise<boolean> {
  if (!feature || !feature.type) {
    throw new Error(`[deeplink] type is required`);
  }

  const type = feature.type;
  type FT = typeof type;

  let params: Params<FT>;
  let named: NamedParams<FT>;

  if ('params' in feature && feature.params) {
    params = feature.params.slice(0, FEATURE_PARAMS[type].length) as Params<FT>;
    named = convertParamsToNamed(type, params);
  } else if ('named' in feature && feature.named) {
    named = feature.named;
    params = convertNamedToParams(type, named);
  } else {
    throw new Error(
      `[deeplink] params (either "params" or "named") are required`,
    );
  }

  const registeredCallbacks = registeredFeatures[type] as
    | FeatureMapping<FT>
    | undefined;

  if (!registeredCallbacks) {
    return false;
  }

  const f = {
    type,
    params,
    named,
  } as FeatureWithParamsAndNamed<FT>;

  const registeredCheck = registeredCallbacks.check;

  if (!registeredCheck) {
    return true;
  }

  if (!areParamsValid(f.type, f.params)) {
    return false;
  }

  return !!(await registeredCheck(f));
}

export async function show<T extends FeatureType>(
  feature: Feature<T> | FeatureWithNamed<T>,
): Promise<void> {
  if (!feature || !feature.type) {
    throw new Error(`[deeplink] type is required`);
  }

  const type = feature.type;
  type FT = typeof type;

  let params: Params<FT>;
  let named: NamedParams<FT>;

  if ('params' in feature && feature.params) {
    params = feature.params.slice(0, FEATURE_PARAMS[type].length) as Params<FT>;
    named = convertParamsToNamed(type, params);
  } else if ('named' in feature && feature.named) {
    named = feature.named as NamedParams<T>;
    params = convertNamedToParams(type, named);
  } else {
    throw new Error(
      `[deeplink] params (either "params" or "named") are required`,
    );
  }

  const registeredCallbacks = registeredFeatures[type] as
    | FeatureMapping<FT>
    | undefined;

  if (!registeredCallbacks) {
    return;
  }

  const f = {
    type,
    params,
    named,
  } as FeatureWithParamsAndNamed<FT>;

  if (!areParamsValid(f.type, f.params)) {
    return;
  }

  const registeredCheck = registeredCallbacks.check;
  const registeredShow = registeredCallbacks.show;

  if (registeredCheck && !(await registeredCheck(f))) {
    return;
  }

  await registeredShow(f);
}
